/**
 * ballMetrics — derive trajectory metrics from a series of ball samples.
 *
 * Input: array of `{ x, y, ts }` (normalized, x/y in [0..1], ts in ms),
 *        ideally captured during the capture window (~3s).
 *
 * Output:
 *   - release_angle_deg : launch angle of the ball above horizontal (°).
 *   - apex_height_norm  : top of the trajectory (1 - minY) [0..1].
 *   - arc_depth_norm    : vertical span of the arc (peak - release).
 *   - flight_time_ms    : ms from release to apex (or last sample).
 *   - distance_to_rim_norm : Euclidean distance from release point to
 *                            rim (only if rim is calibrated).
 *   - rim_made          : true if the ball passes within `rimRadius` of
 *                         the rim point on its descent.
 *
 * Conventions: image coords. y grows DOWN. We invert y for "height".
 */

const MIN_SAMPLES = 5;

/** Smooth a noisy y-series with a 3-tap moving average. */
function smoothY(samples) {
  if (samples.length < 3) return samples.map((s) => s.y);
  const out = new Array(samples.length);
  out[0] = samples[0].y;
  out[samples.length - 1] = samples[samples.length - 1].y;
  for (let i = 1; i < samples.length - 1; i += 1) {
    out[i] = (samples[i - 1].y + samples[i].y + samples[i + 1].y) / 3;
  }
  return out;
}

/**
 * Fit y = a·x² + b·x + c via least squares.
 * Returns { a, b, c } or null if insufficient points.
 */
function fitParabola(pts) {
  if (pts.length < 5) return null;
  let Sx = 0, Sx2 = 0, Sx3 = 0, Sx4 = 0;
  let Sy = 0, Sxy = 0, Sx2y = 0;
  for (const p of pts) {
    const x = p.x, y = p.y;
    const x2 = x * x;
    Sx += x; Sx2 += x2; Sx3 += x2 * x; Sx4 += x2 * x2;
    Sy += y; Sxy += x * y; Sx2y += x2 * y;
  }
  const n = pts.length;
  // 3x3 system: M·[a b c]ᵀ = v
  const M = [
    [Sx4, Sx3, Sx2],
    [Sx3, Sx2, Sx],
    [Sx2, Sx, n],
  ];
  const v = [Sx2y, Sxy, Sy];
  // Cramer's rule
  const det = (m) =>
    m[0][0]*(m[1][1]*m[2][2]-m[1][2]*m[2][1])
    - m[0][1]*(m[1][0]*m[2][2]-m[1][2]*m[2][0])
    + m[0][2]*(m[1][0]*m[2][1]-m[1][1]*m[2][0]);
  const D = det(M);
  if (Math.abs(D) < 1e-12) return null;
  const col = (m, k, vec) => m.map((row, i) => row.map((val, j) => (j === k ? vec[i] : val)));
  const a = det(col(M, 0, v)) / D;
  const b = det(col(M, 1, v)) / D;
  const c = det(col(M, 2, v)) / D;
  return { a, b, c };
}

/**
 * Detect the release index: the first sample after which y monotonically
 * decreases (ball moving up) for at least N consecutive samples.
 */
function findReleaseIdx(samples, ys) {
  const N = 3;
  for (let i = 0; i < samples.length - N; i += 1) {
    let asc = true;
    for (let k = 0; k < N; k += 1) {
      if (ys[i + k + 1] >= ys[i + k]) { asc = false; break; }
    }
    if (asc) return i;
  }
  // Fallback: first sample.
  return 0;
}

/**
 * Detect the apex idx: minimum y (highest point).
 */
function findApexIdx(ys, fromIdx = 0) {
  let bestI = fromIdx, bestY = Infinity;
  for (let i = fromIdx; i < ys.length; i += 1) {
    if (ys[i] < bestY) { bestY = ys[i]; bestI = i; }
  }
  return bestI;
}

/**
 * Compute ball metrics from a trajectory + optional rim calibration.
 *
 * @param {Array<{x:number,y:number,ts:number}>} samples
 * @param {{x:number,y:number}|null} rim - normalized rim center, or null
 * @returns {object} metrics + ok flag
 */
export function computeBallMetrics(samples, rim = null) {
  if (!samples || samples.length < MIN_SAMPLES) {
    return { ok: false, error: 'not_enough_ball_samples' };
  }
  // Sort by timestamp (defensive).
  const pts = [...samples].sort((a, b) => a.ts - b.ts);
  const ys = smoothY(pts);

  const releaseIdx = findReleaseIdx(pts, ys);
  const apexIdx = findApexIdx(ys, releaseIdx);

  const release = { ...pts[releaseIdx], y: ys[releaseIdx] };
  const apex = { ...pts[apexIdx], y: ys[apexIdx] };

  // Release angle: use the first ~150ms after release to estimate the
  // initial velocity direction. Robust to noise via a 2-point look-ahead.
  let releaseAngleDeg = null;
  const dirRef = pts.slice(releaseIdx, releaseIdx + 5);
  if (dirRef.length >= 2) {
    const a = dirRef[0];
    const b = dirRef[dirRef.length - 1];
    const dx = b.x - a.x;
    const dy = ys[releaseIdx + dirRef.length - 1] - ys[releaseIdx];
    if (Math.abs(dx) > 1e-4) {
      // y grows DOWN, so launch angle = atan(-dy / |dx|)
      const slope = -dy / Math.abs(dx);
      releaseAngleDeg = Math.atan(slope) * (180 / Math.PI);
    }
  }

  const apexHeightNorm = 1 - apex.y;
  const arcDepthNorm = release.y - apex.y; // y decreases upward
  const flightTimeMs = apex.ts - release.ts;

  // Distance to rim (Euclidean in normalized space).
  let distanceToRimNorm = null;
  let rimMade = null;
  if (rim) {
    distanceToRimNorm = Math.hypot(rim.x - release.x, rim.y - release.y);
    // Made-shot detection: any descending sample passes within rim radius
    // (assume 5% of frame width as approximate rim radius).
    const RIM_R = 0.04;
    rimMade = false;
    for (let i = apexIdx + 1; i < pts.length; i += 1) {
      const d = Math.hypot(rim.x - pts[i].x, rim.y - ys[i]);
      if (d < RIM_R) { rimMade = true; break; }
    }
  }

  // Parabola fit for arc smoothness; coefficient `a` indicates curvature.
  const fit = fitParabola(pts.slice(releaseIdx).map((p, i) => ({ x: p.x, y: ys[releaseIdx + i] })));
  const arcCurvature = fit ? Math.abs(fit.a) : null;

  // Arc quality score 0..100: ideal angle ~48°, ideal apex above rim.
  let arcQuality = null;
  if (releaseAngleDeg != null) {
    const dist = Math.abs(releaseAngleDeg - 48);
    arcQuality = Math.max(0, Math.min(100, Math.round(100 - dist * 3)));
  }

  return {
    ok: true,
    samples_count: pts.length,
    release_angle_deg: releaseAngleDeg != null ? +releaseAngleDeg.toFixed(1) : null,
    apex_height_norm: +apexHeightNorm.toFixed(3),
    arc_depth_norm: +arcDepthNorm.toFixed(3),
    arc_curvature: arcCurvature != null ? +arcCurvature.toFixed(2) : null,
    arc_quality_score: arcQuality,
    flight_time_ms: flightTimeMs > 0 ? +flightTimeMs.toFixed(0) : null,
    distance_to_rim_norm: distanceToRimNorm != null ? +distanceToRimNorm.toFixed(3) : null,
    rim_made: rimMade,
    release_point: { x: +release.x.toFixed(3), y: +release.y.toFixed(3) },
    apex_point: { x: +apex.x.toFixed(3), y: +apex.y.toFixed(3) },
    trajectory: pts.map((p, i) => ({
      x: +p.x.toFixed(3),
      y: +ys[i].toFixed(3),
      t: Math.round(p.ts - pts[0].ts),
    })),
  };
}

/**
 * Derive ball-specific errors from computed metrics.
 * Combined with pose errors in the final analyzer.
 */
export function ballErrors(m) {
  const errs = [];
  if (m.release_angle_deg != null) {
    if (m.release_angle_deg < 38) errs.push('flat_arc');
    else if (m.release_angle_deg > 58) errs.push('rainbow_arc');
  }
  if (m.arc_depth_norm != null && m.arc_depth_norm < 0.08) {
    errs.push('flat_arc');
  }
  return Array.from(new Set(errs));
}
